/**
 * YouTube IFrame API에서 mioSegno가 실제로 사용하는 최소 플레이어 계약입니다.
 * 공유 연습 화면과 세뇨 편집 화면이 같은 로더와 타입을 사용하도록 분리했습니다.
 */
export interface YTPlayer {
  playVideo(): void
  pauseVideo(): void
  seekTo(seconds: number, allowSeekAhead: boolean): void
  getCurrentTime(): number
  getDuration(): number
  setVolume(volume: number): void
  destroy(): void
}

export interface YTNamespace {
  Player: new (element: HTMLElement, options: {
    videoId: string
    playerVars: Record<string, number>
    events: {
      onReady: () => void
      onStateChange: (event: { data: number }) => void
      onError?: (event: { data: number }) => void
    }
  }) => YTPlayer
  PlayerState: { PLAYING: number; PAUSED: number; ENDED: number }
}

declare global {
  interface Window {
    YT?: YTNamespace
    onYouTubeIframeAPIReady?: () => void
  }
}

let iframeApiPromise: Promise<YTNamespace> | null = null

/**
 * 페이지에 YouTube IFrame API 스크립트를 한 번만 추가합니다.
 * 두 화면이 각각 로더를 만들면 전역 콜백을 덮어쓸 수 있으므로 반드시 이 함수를 공유합니다.
 */
export function loadYouTubeIframeApi(): Promise<YTNamespace> {
  if (window.YT?.Player) return Promise.resolve(window.YT)
  if (iframeApiPromise) return iframeApiPromise

  const loading = new Promise<YTNamespace>((resolve, reject) => {
    let settled = false
    let timeoutId: number | null = null
    const finishResolve = () => {
      if (settled) return
      settled = true
      if (timeoutId != null) window.clearTimeout(timeoutId)
      if (window.YT?.Player) resolve(window.YT)
      else reject(new Error('YouTube API unavailable'))
    }
    const finishReject = (message: string) => {
      if (settled) return
      settled = true
      if (timeoutId != null) window.clearTimeout(timeoutId)
      reject(new Error(message))
    }

    const previous = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => {
      try {
        previous?.()
      } finally {
        finishResolve()
      }
    }

    const existing = document.querySelector('script[data-miosegno-youtube-api]')
    if (!existing) {
      const script = document.createElement('script')
      script.src = 'https://www.youtube.com/iframe_api'
      script.async = true
      script.dataset.miosegnoYoutubeApi = 'true'
      script.onerror = () => finishReject('YouTube API load failed')
      document.head.appendChild(script)
    }

    timeoutId = window.setTimeout(() => finishReject('YouTube API load timeout'), 15000)
  })

  iframeApiPromise = loading.catch((error) => {
    // 일시적인 네트워크 실패 뒤 사용자가 주소를 다시 입력하면 재시도할 수 있게 합니다.
    iframeApiPromise = null
    throw error
  })
  return iframeApiPromise
}
